import { useDeliveryLocationCookie } from '@/components/content/Header/parts/DeliveryLocator/hooks/useDeliveryLocationCookie';
import { BYPASSES_CUTOFF, EddConfig, getEddConfig } from '@/data/config/eddConfig';
import { useDefaultStore } from '@/data/Content/DefaultStore';
import { useLeadTime } from '@/data/Content/LeadTime';
import { useSettings } from '@/data/Settings';
import { ProductType } from '@/data/types/Product';
import { calculateEddDate, isExcludedDay, isPastCutoff } from '@/data/utils/eddDateCalculation';
import { getIndexedQuantity, getMarketAvailability } from '@/data/utils/eddInventory';
import {
	isPickupOnlyEnabled,
	isProductFlagEnabled,
	isSameDayFlagEnabled,
} from '@/data/utils/eddProductFlags';
import { getProductMinQuantity } from '@/data/utils/getProductMinQuantity';
import { useIsClient } from '@/data/utils/useIsClient';
import { useMemo } from 'react';
import useSWR from 'swr';
import { getFulfillmentType } from 'shared/custom/utils/inventoryUtils';

const fetcher = (url: string) => fetch(url).then((response) => response.json());

/**
 * Lean read of the same blocked-zip-code list `useAddressBook` exposes, without
 * pulling in its person/address data fetching — this hook renders once per PLP
 * product card, so it avoids the heavier hook. Same SWR key, so the cache is shared.
 */
export const useBlockedZipCodes = (
	enabled: boolean
): { blockedZipCodes: string[]; isLoading: boolean } => {
	const { data, isLoading } = useSWR(enabled ? '/api/blocked-zip-codes' : null, fetcher);
	return { blockedZipCodes: data?.zipCodeBlockList ?? [], isLoading: !!isLoading };
};

export type HomeDeliveryDateState =
	/**
	 * Still resolving — distinct from `no-label`, which is a settled "render nothing".
	 * The label shows a skeleton for this so the customer never sees partial text
	 * before the real result lands. Reachable before hydration (AC25) and while the
	 * blocked-zip list is in flight, since AC8 forbids promising a date until the zip
	 * rule can be evaluated.
	 */
	| { status: 'pending' }
	| { status: 'blocked-zip' }
	| { status: 'not-available' }
	| { status: 'no-label' }
	| {
			status: 'with-date';
			descriptor: 'today' | 'tomorrow' | 'date';
			formatted: string;
			deliveryDate: Date;
			/**
			 * Set only by the Same Day branch past its cutoff (163024 AC17): the date is
			 * today + EDD_SAMEDAY_AFTER_CUTOFF_DAYS, so the home delivery cutoff did not
			 * decide it and its legend does not apply. Still an ordinary delivery (AC21).
			 */
			sameDayPastCutoff?: true;
	  }
	/**
	 * Same Day story 163024 AC15/AC20: an express promise, always for today. Kept
	 * apart from a `with-date` that also lands today so the component can write a
	 * different text for each; the fields keep their `with-date` meaning (AC23).
	 */
	| {
			status: 'same-day';
			descriptor: 'today';
			formatted: string;
			deliveryDate: Date;
	  };

/**
 * Home delivery resolution for the PLP EDD label (story 159042 AC8-AC11): blocked
 * zip code takes precedence, then the product-level `bopisOnly` exclusivity flag,
 * then lead time availability, then the shared cutoff + excluded-day date
 * calculation (story 158621).
 *
 * The first two states are deliberately distinct and carry different copy: a
 * blocked zip code is a location rule ("Envío limitado en este código postal",
 * story 157814 AC2), while `bopisOnly` is a product rule ("Exclusivo para recoger
 * en tienda", story 157722 AC12 — which scopes itself to delivery blocked "for any
 * reason except Blocked Zip Code"). Zip is evaluated first per 159042 AC8, which
 * puts it before any other condition in the channel.
 *
 * AC24/AC25/AC28: resolution must happen on the client at hydration, never
 * server-rendered/cached — the cutoff check depends on the customer's browser
 * clock and timezone, which the server doesn't have. `useIsClient` holds this
 * at the reserved-height empty state until after mount.
 */
export type UseHomeDeliveryDateOptions = {
	checkMarketAvailability?: boolean;
	/**
	 * Same Day (163024 AC11) store inventory, for surfaces whose inventory does not
	 * come from the search index (the PDP availability call). When omitted, it is
	 * read from the indexed `inventories.<uniqueId>.quantity`, as on PLP.
	 */
	storeAvailability?: number;
};

export type HomeDeliveryResolutionInputs = {
	zipCode: string;
	blockedZipCodes: string[];
	isBopisOnly: boolean;
	checkMarketAvailability: boolean;
	/** `undefined` means the level is unknown, not that it's zero — see call sites. */
	marketAvailability: number | undefined;
	minQuantity: number;
	leadTime: number | undefined;
	fulfillmentType: string | undefined;
	/** 163024 AC11-AC13: `x_itemsameday.<storeId>` for the selected store. */
	isSameDayFlagOn: boolean;
	/** 163024 AC11: inventory in the selected store; `undefined` (unknown) is not eligible. */
	storeAvailability: number | undefined;
};

/**
 * Same Day branch (story 163024 AC8, AC11-AC19) — step 3 of the home delivery
 * tree, after the blocking conditions. Returns `null` when any eligibility
 * condition fails so the caller goes down the normal lead time path untouched.
 *
 * Dropship/Special Order never hold sales-floor inventory, so they are filtered
 * out by the inventory condition rather than by a rule of their own (AC14).
 */
const resolveSameDayState = (
	config: EddConfig,
	inputs: HomeDeliveryResolutionInputs,
	now: Date = new Date()
): HomeDeliveryDateState | null => {
	const { sameDay } = config;
	// AC5: master switch off, nothing resolves Same Day.
	if (!sameDay || !inputs.isSameDayFlagOn) return null;

	// AC11: an express promise is only made on known store inventory.
	const hasStoreInventory =
		inputs.storeAvailability !== undefined && inputs.storeAvailability >= inputs.minQuantity;
	if (!hasStoreInventory) return null;

	// AC19: an excluded day of the Same Day calendar falls to the normal path, as
	// if the flag were off — it does not resolve the AC17 past-cutoff date.
	if (isExcludedDay(now, sameDay.excludedWeekdays, sameDay.excludedDates)) return null;

	if (!isPastCutoff(sameDay.cutoffTime, now)) {
		// AC15/AC16: today, without reading the lead time. Today was already checked
		// against the Same Day calendar above, so the delivery calendar is skipped.
		const today = calculateEddDate(
			{ daysToAdd: 0, channel: 'delivery', skipExclusions: true },
			config,
			now
		);
		return today
			? {
					status: 'same-day',
					descriptor: 'today',
					formatted: today.formatted,
					deliveryDate: today.date,
			  }
			: null;
	}

	// AC17/AC18/AC21: past the cutoff the express promise is dropped — an ordinary
	// delivery date, shifted by the home delivery calendar.
	const result = calculateEddDate(
		{ daysToAdd: sameDay.afterCutoffDays, channel: 'delivery' },
		config,
		now
	);
	if (!result) return { status: 'no-label' };
	return {
		status: 'with-date',
		descriptor: result.descriptor,
		formatted: result.formatted,
		deliveryDate: result.date,
		sameDayPastCutoff: true,
	};
};

/**
 * The full home-delivery resolution tree (story 159042 AC8-AC11), independent of
 * where its inputs came from — the counterpart of `resolvePickupDateState`. The
 * hook below derives these inputs for a single product; the tree itself is reused
 * as-is, never duplicated or forked, only the derivation differs by surface.
 *
 * The `pending` states stay in the hook: they describe whether the inputs are
 * ready at all, not what they resolve to.
 */
// eslint-disable-next-line complexity
export const resolveHomeDeliveryDateState = (
	config: EddConfig,
	inputs: HomeDeliveryResolutionInputs
): HomeDeliveryDateState => {
	// PICK UP ONLY (AC19-22): a global switch, so it's checked before anything
	// else -- if it's on, delivery is unavailable for every product regardless
	// of zip, bopisOnly, or leadtime, and none of those need to be evaluated.
	if (isPickupOnlyEnabled(config)) {
		return { status: 'not-available' };
	}

	if (inputs.blockedZipCodes.includes(inputs.zipCode)) {
		return { status: 'blocked-zip' };
	}
	if (inputs.isBopisOnly) {
		return { status: 'not-available' };
	}

	// Same Day (163024 AC8): step 3, after the blocking conditions and before the
	// normal lead time path, which stays the fallback (AC16).
	const sameDayState = resolveSameDayState(config, inputs);
	if (sameDayState) {
		return sameDayState;
	}

	if (inputs.checkMarketAvailability) {
		const hasMarketAvailability =
			inputs.marketAvailability === undefined
				? true
				: inputs.marketAvailability >= inputs.minQuantity;
		if (!hasMarketAvailability) {
			return { status: 'no-label' };
		}
	}

	if (inputs.leadTime === undefined) {
		return { status: 'no-label' };
	}

	const bypassesCutoff = !!inputs.fulfillmentType && BYPASSES_CUTOFF.has(inputs.fulfillmentType);
	const daysToAdd =
		!bypassesCutoff && isPastCutoff(config.deliveryCutoffTime)
			? inputs.leadTime + config.deliveryAfterCutoffDays
			: inputs.leadTime;

	const result = calculateEddDate(
		{ daysToAdd, channel: 'delivery', skipExclusions: bypassesCutoff },
		config
	);
	if (!result) {
		return { status: 'no-label' };
	}
	return {
		status: 'with-date',
		descriptor: result.descriptor,
		formatted: result.formatted,
		deliveryDate: result.date,
	};
};

/**
 * LeadTime/NominalQuantity.md: a regular SKU sold in multiples needs at least its
 * minimum quantity at a level to count as available there. Anything that is not a
 * real multiple (1, absent, unparseable) keeps the plain "> 0" rule.
 */
export const getEddMinQuantity = (product: ProductType | undefined): number => {
	const nominalQuantity = product ? getProductMinQuantity(product) : 1;
	return Number.isFinite(nominalQuantity) && nominalQuantity > 1 ? nominalQuantity : 1;
};

export const useHomeDeliveryDate = (
	product: ProductType | undefined,
	physicalStoreId: string,
	{ checkMarketAvailability = true, storeAvailability }: UseHomeDeliveryDateOptions = {}
): HomeDeliveryDateState => {
	const isClient = useIsClient();
	// The store-level inventory key is the physical store's uniqueId, as in `usePickupDate`.
	const { uniqueId } = useDefaultStore();
	const leadTime = useLeadTime(product, physicalStoreId);
	const { zipCode } = useDeliveryLocationCookie();
	const { blockedZipCodes, isLoading: isLoadingBlockedZipCodes } = useBlockedZipCodes(!!product);
	const { settings } = useSettings();
	const fulfillmentType = useMemo(
		() =>
			getFulfillmentType(
				product?.attributes,
				(product?.ribbons ?? []).map((ribbon) => ribbon.identifier)
			),
		[product?.attributes, product?.ribbons]
	);
	return useMemo(() => {
		// AC25: nothing is resolved server-side. Reported as `pending` rather than
		// `no-label` so the label can show a skeleton instead of an empty row.
		if (!isClient) {
			return { status: 'pending' };
		}

		const config = getEddConfig(settings.userData);

		// PICK UP ONLY (AC19-22) settles every product at once regardless of zip,
		// bopisOnly or leadtime, so it is resolved ahead of the wait below.
		if (isPickupOnlyEnabled(config)) {
			return { status: 'not-available' };
		}

		// AC8 puts the zip rule first, so nothing may be promised until the block list
		// has resolved — otherwise a blocked customer is shown a delivery date on first
		// paint that then disappears, which also breaks AC29's no-shift guarantee.
		if (isLoadingBlockedZipCodes) {
			return { status: 'pending' };
		}

		return resolveHomeDeliveryDateState(config, {
			zipCode,
			blockedZipCodes,
			isBopisOnly: isProductFlagEnabled(product?.bopisOnly),
			checkMarketAvailability,
			marketAvailability: getMarketAvailability(product, physicalStoreId),
			minQuantity: getEddMinQuantity(product),
			leadTime,
			fulfillmentType,
			isSameDayFlagOn: isSameDayFlagEnabled(product, physicalStoreId),
			storeAvailability: storeAvailability ?? getIndexedQuantity(product, uniqueId),
		});
	}, [
		isClient,
		uniqueId,
		storeAvailability,
		isLoadingBlockedZipCodes,
		blockedZipCodes,
		zipCode,
		product,
		physicalStoreId,
		leadTime,
		fulfillmentType,
		settings.userData,
		checkMarketAvailability,
	]);
};
